// process.env.API_BASE is inlined at build time (see build.mjs). Empty
// string means "same origin" — the Node server serves the built frontend
// and the API from the same port in production.
declare const process: { env: { API_BASE: string } };
const API_BASE = process.env.API_BASE || "";

// Property/tenancy photos come back from the API as server-relative paths
// (e.g. "/uploads/prop_x/photo_y.jpg"). On the website that resolves fine
// against the page's own origin; inside the native app the WebView's origin
// isn't the real server, so relative paths resolve nowhere. Idempotent —
// safe to call on data: URIs or already-absolute URLs, which pass through
// unchanged (staged/Aadhaar photos are raw data: URIs, never server paths).
export function resolveUrl(path: string): string {
  if (!path || path.startsWith("data:") || /^https?:\/\//.test(path)) return path;
  return API_BASE + path;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let currentToken: string | null = null;
export function setToken(token: string | null) {
  currentToken = token;
  if (token) localStorage.setItem("toletbook_token", token);
  else localStorage.removeItem("toletbook_token");
}
export function loadToken(): string | null {
  // sessionStorage (per-tab) takes priority over localStorage (shared
  // across tabs) — lets a specific tab hold its own session without
  // disturbing the "stay logged in" token every other tab relies on.
  currentToken = sessionStorage.getItem("toletbook_token") || localStorage.getItem("toletbook_token");
  return currentToken;
}

async function request(method: string, path: string, body?: unknown) {
  const res = await fetch(API_BASE + path, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, data?.error || `Request failed (${res.status})`);
  }
  return data;
}

export const api = {
  get: (path: string) => request("GET", path),
  post: (path: string, body?: unknown) => request("POST", path, body),
  patch: (path: string, body?: unknown) => request("PATCH", path, body),
  del: (path: string, body?: unknown) => request("DELETE", path, body),
};

// ---------- Types ----------
export interface User {
  id: string;
  role: "owner" | "tenant";
  phone: string | null;
  name: string;
  email: string;
  city?: string | null;
  accountType?: string | null;
  verificationStatus?: "unverified" | "pending" | "verified" | "rejected";
  verificationNote?: string | null;
  bankAccountHolder?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  upiId?: string | null;
  referralCode?: string | null;
  digilockerVerifiedName?: string | null;
  digilockerVerifiedAt?: string | null;
  deletionRequestedAt?: string | null;
  badgeVerified?: boolean;
  estampInterestAt?: string | null;
  emailVerified?: boolean;
}

export interface ReferralEntry {
  refereeName: string;
  status: "pending" | "rewarded" | "referee_only";
  createdAt: string;
}

export interface ReferralStats {
  code: string;
  cap: number;
  rewardedCount: number;
  remaining: number;
  freeMonthsOwed: number;
  referrals: ReferralEntry[];
}

export interface ClaimReferralReward {
  subscriptionId: string;
  keyId: string | null;
  freeMonths: number;
  trialEndsAt: string;
}

export interface Tenancy {
  id: string;
  tenantId: string | null;
  unitId: string | null;
  name: string;
  phone: string | null;
  aadhaar: string | null;
  aadhaarPhotoFront: string | null;
  aadhaarPhotoBack: string | null;
  rent: number;
  deposit: number;
  rentDueDay: number;
  noticePeriodDays: number;
  onboardingStatus: "pending" | "completed";
  depositStatus: "pending" | "paid";
  depositPaidOn: string | null;
  depositPaymentMode: "online" | "cash" | null;
  depositProofPhoto: string | null;
  digilockerVerifiedName?: string | null;
  digilockerVerifiedAt?: string | null;
  status: "active" | "ended";
  startDate: string;
  property?: Property | null;
}

export interface TenancyExit {
  id: string;
  tenancyId: string;
  exitDate: string;
  depositAmount: number;
  deductions: number;
  deductionNote: string;
  refundAmount: number;
  createdAt: string;
  property?: Property | null;
}

export interface RentalAgreementStatus {
  id: string;
  tenancyId: string;
  stateCode: string;
  stampStatus: "not_started" | "placed" | "assigned" | "generating" | "verifying" | "verified" | "cancelled";
  stampCertificateUrl: string | null;
  esignStatus: "not_started" | "pending" | "owner_signed" | "tenant_signed" | "completed" | "failed";
  signedDocumentUrl: string | null;
  feeStatus: "not_started" | "pending" | "paid";
  feeAmount: number | null;
  interestRegisteredAt: string | null;
  uploadedDocumentUrl: string | null;
  uploadedDocumentName: string | null;
  uploadedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Bed {
  id: string;
  label: string;
  toLet: boolean;
  tenancy: Tenancy | null;
}

export interface Room {
  id: string;
  sharingType: string;
  rentPerBed: number;
  depositPerBed: number;
  hasAc: boolean;
  hasAttachedBathroom: boolean;
  beds: Bed[];
}

export interface Subscription {
  plan: string;
  status: "trialing" | "active" | "expired" | "paused" | "none";
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  limits: Record<string, boolean | number>;
  // Owners only — how many PG beds they run against their plan's cap.
  pg?: {
    beds: number;
    bedCap: number | null; // null = no cap
    slab: PgSlab | null;
    grandfathered: boolean;
    graceUntil: string | null;
    recommendedSlab: PgSlab | null;
  };
}

export type PgSlab = "starter" | "standard" | "plus" | "large";

export interface PgPlans {
  freeBeds: number;
  premiumSurcharge: { monthly: number; yearly: number };
  slabs: { key: PgSlab; minBeds: number; maxBeds: number | null; prices: { monthly: number; yearly: number }; available: boolean }[];
}

export interface LedgerCharge {
  id: string;
  kind: "electricity" | "late_fee" | "other";
  label: string;
  amount: number;
  createdAt: string;
}

export interface Property {
  id: string;
  ownerId: string;
  ownerName: string | null;
  ownerPhone: string | null;
  ownerUpiId?: string | null;
  ownerBankAccountHolder?: string | null;
  ownerBankAccountNumber?: string | null;
  ownerBankIfsc?: string | null;
  ownerVerified: boolean;
  ownerReferralBadge: boolean;
  ownerPlan: "basic" | "pro" | "premium";
  featured: boolean;
  topListing: boolean;
  boosted: boolean;
  featuredUntil: string | null;
  lateFeeAmount?: number | null; // owner view: flat fine added to rent still unpaid after the grace period
  lateFeeGraceDays?: number;
  phoneLocked?: boolean;
  unlockNeedsUpgrade?: boolean;
  category: "independent" | "pg" | "commercial";
  label: string;
  locality: string;
  city: string | null;
  state: string | null;
  pincode: string | null;
  type: string | null;
  occupancyType: string | null;
  facilities: string[];
  mealPlan: string[];
  preferredTenants: string[];
  furnishingStatus: "unfurnished" | "semi_furnished" | "fully_furnished" | null;
  communityType: "gated" | "semi_gated" | "villa" | "apartment" | "independent" | null;
  floorNumber: string | null;
  furnishingItems: Record<string, number>;
  carpetAreaSqft: number | null;
  builtUpAreaSqft: number | null;
  lockInMonths: number | null;
  rentEscalationPercent: number | null;
  rentEscalationYears: number | null;
  powerBackup: boolean | null;
  powerLoadDetails: string | null;
  parkingSpots: number | null;
  washroomType: "attached" | "shared" | null;
  gstRegistrationPossible: boolean | null;
  frontageSignage: boolean | null;
  possessionStatus: "ready" | "under_fitout" | null;
  seatsCount: number | null;
  cabinsCount: number | null;
  meetingRoomsCount: number | null;
  toLet: boolean;
  expectedRent: number | null;
  expectedDeposit: number | null;
  maintenance: number | null;
  photos: string[];
  location: { lat: number; lng: number; label: string } | null;
  locationSource?: string | null;
  locationApproximate?: boolean;
  createdAt: string;
  status?: "vacant" | "occupied";
  tenancy?: Tenancy | null;
  rooms?: Room[];
  units?: PropertyUnit[];
  occupancySummary?: { totalBeds: number; vacantBeds: number; occupiedBeds: number };
}

// A standalone house/flat within an independent property that has more than
// one (e.g. a compound with House 1, House 2) — as opposed to the property's
// own expectedRent/tenancy, which is used directly when there's just one.
export interface PropertyUnit {
  id: string;
  label: string;
  type: string | null;
  expectedRent: number | null;
  expectedDeposit: number | null;
  maintenance: number | null;
  furnishingStatus: "unfurnished" | "semi_furnished" | "fully_furnished" | null;
  communityType: "gated" | "semi_gated" | "villa" | "apartment" | "independent" | null;
  floorNumber: string | null;
  furnishingItems: Record<string, number>;
  toLet: boolean;
  tenancy: Tenancy | null;
}

export interface LedgerEntry {
  id: string;
  tenancyId: string;
  month: string;
  amount: number; // total due: rent plus any charges
  rentAmount?: number;
  chargesTotal?: number;
  charges?: LedgerCharge[];
  status: "paid" | "pending" | "pending_confirmation" | "overdue";
  mode: string | null;
  paidOn: string | null;
  reminderSentAt: string | null;
  paymentReference?: string | null;
  claimNote?: string | null;
  claimedAt?: string | null;
  rejectionReason?: string | null;
}

export type RequestCategory = "plumbing" | "electrical" | "appliance" | "furniture" | "cleaning" | "pest_control" | "other";

export interface MaintenanceRequest {
  id: string;
  tenancyId: string;
  propertyId: string | null;
  title: string;
  description: string;
  category: RequestCategory | null;
  urgency: "normal" | "urgent";
  photo: string | null;
  status: "open" | "in_progress" | "resolved";
  raisedAt: string;
  updatedAt: string;
}

export interface RequestComment {
  id: string;
  requestId: string;
  authorId: string;
  authorRole: "owner" | "tenant";
  authorName: string;
  body: string;
  createdAt: string;
}

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  url: string | null;
  read: boolean;
  createdAt: string;
}

export interface VisitRequest {
  id: string;
  propertyId: string;
  tenantId: string;
  date: string;
  time: string;
  note: string;
  status: "pending" | "confirmed" | "declined" | "cancelled";
  createdAt: string;
  propertyLabel?: string;
  propertyLocality?: string;
  tenantName?: string;
  tenantPhone?: string;
  tenantVerified?: boolean;
}

export interface VacateRequest {
  id: string;
  tenancyId: string;
  propertyId: string | null;
  noticeDate: string;
  vacateDate: string;
  note: string;
  status: "pending" | "acknowledged" | "cancelled";
  createdAt: string;
  propertyLabel?: string;
  propertyLocality?: string;
  tenantName?: string;
  tenantPhone?: string;
}
